import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { MapPin, Clock, ArrowLeft, ExternalLink, Loader2, AlertCircle } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { formatSalary } from "../components/JobCard";
import { stripMarkdown } from "../utils/textFormat";
import { track } from "../lib/analytics";
import { AdzunaAttribution } from "../components/AdzunaAttribution";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Adzuna's own raw enum values — translated for display only, never invented
// (None from Adzuna stays hidden, nothing is inferred from title/description).
const CONTRACT_TIME_LABELS = { full_time: "scheduleFullTime", part_time: "schedulePartTime" };
const CONTRACT_TYPE_LABELS = { permanent: "contractPermanent", contract: "contractTemporary" };

/* Detalle de un empleo EXTERNO (Adzuna). Solo lectura — sin aplicacion interna,
   sin guardar, sin match_score. El CTA redirige al redirect_url original que
   el backend valido contra el allowlist de dominios de Adzuna. */
export default function ExternalJobDetailPage() {
  const { id } = useParams(); // formato "adzuna:<external_id>"
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const externalId = id?.split(":")[1] || "";

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/external-jobs/${externalId}`)
      .then((res) => { if (mounted) { setJob(res.data); track("external_job_open", { provider: "adzuna", external_id: res.data.external_id, country: res.data.country_code, city: res.data.location }); } })
      .catch(() => { if (mounted) setError(true); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalId]);

  const handleRedirect = () => {
    if (!job?.redirect_url) return;
    track("external_job_redirect", { provider: "adzuna", external_id: job.external_id, country: job.country_code, city: job.location });
    window.open(job.redirect_url, "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="external-job-loading">
        <Loader2 className="h-8 w-8 text-[#028CF8] animate-spin" />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 px-6 text-center" data-testid="external-job-not-found">
        <AlertCircle className="h-10 w-10 text-[#F55FA5]" />
        <p className="font-poppins font-semibold text-[#0F172A]">{t("jobDetail.jobNotFound") || "No encontramos esta oferta"}</p>
        <Link to="/jobs" className="text-[#028CF8] font-semibold text-sm hover:underline">{t("jobDetail.backToJobs") || "Volver a empleos"}</Link>
      </div>
    );
  }

  const hasSalary = job.salary_min || job.salary_max;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="external-job-detail-page">
      <div className="bg-gradient-to-br from-[#028CF8] to-[#0270C5] pt-6 pb-8 px-4 sm:px-8">
        <button onClick={() => navigate(-1)} data-testid="external-job-back-button"
          className="flex items-center gap-1.5 text-white/90 text-sm font-poppins mb-5 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> {t("jobDetail.back") || "Volver"}
        </button>
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-8 w-8 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center font-poppins font-bold text-white text-xs flex-shrink-0">
              {job.company_name?.[0]?.toUpperCase() || "J"}
            </div>
            <p className="text-white font-semibold text-sm truncate" data-testid="external-job-company">{job.company_name}</p>
          </div>
          <h1 className="font-poppins text-xl sm:text-2xl font-bold text-white leading-tight break-words mb-2" data-testid="external-job-title">
            {job.title}
          </h1>
          <p className="flex items-center gap-1.5 text-sm text-white/90 mb-3">
            <MapPin className="h-3.5 w-3.5 flex-shrink-0" /> {job.location}
            {job.posted_date && <><span className="opacity-50">·</span><Clock className="h-3.5 w-3.5" />{new Date(job.posted_date).toLocaleDateString()}</>}
          </p>
          {hasSalary && (
            <span className="inline-block text-xs font-bold px-3 py-1.5 rounded-full bg-white/15 text-white mb-2">
              {formatSalary(job.salary_min, job.salary_max, job.salary_currency)}
            </span>
          )}
          {/* Campos reales adicionales que Adzuna entrega — nunca inventados,
              solo se muestran si vinieron con valor. */}
          {(job.contract_time_raw || job.contract_type_raw || job.category_original) && (
            <div className="flex flex-wrap gap-2 mb-2" data-testid="external-job-meta-chips">
              {job.contract_time_raw && CONTRACT_TIME_LABELS[job.contract_time_raw] && (
                <span className="inline-block text-xs font-semibold px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/90" data-testid="external-job-schedule-chip">
                  {t(`jobDetail.${CONTRACT_TIME_LABELS[job.contract_time_raw]}`)}
                </span>
              )}
              {job.contract_type_raw && CONTRACT_TYPE_LABELS[job.contract_type_raw] && (
                <span className="inline-block text-xs font-semibold px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/90" data-testid="external-job-contract-chip">
                  {t(`jobDetail.${CONTRACT_TYPE_LABELS[job.contract_type_raw]}`)}
                </span>
              )}
              {job.category_original && (
                <span className="inline-block text-xs font-semibold px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/90" data-testid="external-job-category-chip">
                  {job.category_original}
                </span>
              )}
            </div>
          )}
          <div className="mt-3">
            <AdzunaAttribution testId="external-job-adzuna-attribution" onDark />
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-6">
        <div className="bg-white border border-slate-100 rounded-2xl p-6 sm:p-7 mb-24" data-testid="external-job-description">
          <h2 className="font-poppins font-bold text-[#0F172A] mb-1">{t("jobDetail.descriptionPreview")}</h2>
          <p className="text-xs text-slate-400 font-poppins mb-4">{t("jobDetail.descriptionPreviewCaption")}</p>
          <div className="space-y-3">
            {(stripMarkdown(job.description) || "").split(/\n+/).filter((p) => p.trim()).map((paragraph, i) => (
              <p key={i} className="text-slate-600 text-sm leading-7 break-words">{paragraph}</p>
            ))}
          </div>
        </div>
      </div>

      {/* CTA sticky — nunca aplicacion interna para empleos externos */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-3 sm:px-8 z-40">
        <div className="max-w-3xl mx-auto">
          <button
            onClick={handleRedirect}
            data-testid="external-job-redirect-cta"
            className="w-full h-12 bg-[#028CF8] hover:bg-[#0270C5] text-white font-poppins font-semibold rounded-xl text-sm transition-all shadow-lg flex items-center justify-center gap-2"
          >
            <ExternalLink className="h-4 w-4" /> {t("jobDetail.viewOfferExternal") || "Ver oferta / Postular en sitio externo"}
          </button>
        </div>
      </div>
    </div>
  );
}
